import { useState } from "react";
import { useForm } from "react-hook-form";
import { LuCamera } from "react-icons/lu";
import InputField from "../../components/ui/InputField";
import { useToast } from "../../hooks/useToast";
import { useAuth } from "../../hooks/useAuth";

interface ProfileFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    username: string;
    avatar?: FileList;
}

export default function ProfileTab() {
    const { user } = useAuth();
    
    const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<ProfileFormData>({
        defaultValues: {
            firstName: user?.firstName || "",
            lastName: user?.lastName || "",
            email: user?.email || "",
            phone: "",
            username: "",
        },
    });

    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const onSubmit = async (data: ProfileFormData) => {
        setIsLoading(true);
        try {
            // Simulate API call
            await new Promise((resolve) => setTimeout(resolve, 1000));

            console.log("Form Data:", data);

            showToast({
                title: "Success!",
                message: "Your profile has been updated successfully.",
                variant: "success",
                duration: 3000,
            });

            reset(data); // reset to new values so isDirty becomes false
        } catch {
            showToast({
                title: "Error!",
                message: "Failed to update profile. Please try again.",
                variant: "error",
                duration: 3000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        reset();
        setAvatarPreview(null);
        showToast({
            title: "Cancelled",
            message: "Changes have been discarded.",
            variant: "info",
            duration: 2000,
        });
    };

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">Personal Information</h3>
                <p className="text-gray-500 text-sm">Update your avatar and personal details here.</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                
                {/* Avatar Section */}
                <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center py-4">
                    <div className="relative group">
                        <div className={`w-24 h-24 rounded-3xl ${avatarPreview ? "" : "bg-gradient-to-tr from-violet-600 to-fuchsia-600"}
                                flex items-center justify-center text-white font-bold text-2xl shrink-0 overflow-hidden shadow-lg border-4 border-white`}>
                            {avatarPreview ? (
                                <img src={avatarPreview} alt="Avatar preview" className="w-full h-full object-cover" />
                            ) : (
                                (user?.firstName?.charAt(0) || "U").toUpperCase()
                            )}
                        </div>
                        <div className="absolute inset-0 bg-black/40 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                            <LuCamera size={24} className="text-white" />
                        </div>
                    </div>
                    
                    <div className="flex-1">
                        <InputField
                            type="media"
                            id="avatar-input"
                            icon={<LuCamera />}
                            placeholder="Upload New Avatar"
                            disabled={isLoading}
                            {...register("avatar", {
                                onChange: handleAvatarChange,
                            })}
                        />
                        <p className="text-xs text-gray-400 mt-2">JPG, GIF or PNG. 1MB max.</p>
                    </div>
                </div>

                {/* Form Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-7 border-t border-gray-100 pt-8">
                    <div>
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">First Name</label>
                        <InputField
                            type="text"
                            placeholder="e.g. John"
                            disabled={isLoading}
                            className="bg-gray-50 border-gray-200 focus:border-violet-500 py-3 rounded-xl"
                            error={errors.firstName?.message}
                            {...register("firstName", {
                                required: "First name is required",
                                minLength: { value: 2, message: "Must be at least 2 characters" },
                            })}
                        />
                    </div>
                    
                    <div>
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Last Name</label>
                        <InputField
                            type="text"
                            placeholder="e.g. Doe"
                            disabled={isLoading}
                            className="bg-gray-50 border-gray-200 focus:border-violet-500 py-3 rounded-xl"
                            error={errors.lastName?.message}
                            {...register("lastName", {
                                required: "Last name is required",
                            })}
                        />
                    </div>
                    
                    <div className="md:col-span-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Email Address</label>
                        <InputField
                            type="email"
                            placeholder="john@example.com"
                            disabled={isLoading}
                            className="bg-gray-50 border-gray-200 focus:border-violet-500 py-3 rounded-xl"
                            error={errors.email?.message}
                            {...register("email", {
                                required: "Email is required",
                                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email format" },
                            })}
                        />
                    </div>
                    
                    <div>
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Phone Number <span className="opacity-50">(Optional)</span></label>
                        <InputField
                            type="phone"
                            placeholder="+1 (555) 000-0000"
                            disabled={isLoading}
                            className="bg-gray-50 border-gray-200 focus:border-violet-500 py-3 rounded-xl"
                            error={errors.phone?.message}
                            {...register("phone")}
                        />
                    </div>
                    
                    <div>
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Username <span className="opacity-50">(Optional)</span></label>
                        <InputField
                            type="text"
                            placeholder="@username"
                            disabled={isLoading}
                            className="bg-gray-50 border-gray-200 focus:border-violet-500 py-3 rounded-xl"
                            error={errors.username?.message}
                            {...register("username")}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isLoading || (!isDirty && !avatarPreview)}
                        className="px-6 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        disabled={isLoading || (!isDirty && !avatarPreview)}
                        className="px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-violet-200 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 transition-all flex items-center gap-2"
                    >
                        {isLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                        Save Changes
                    </button>
                </div>
            </form>
        </div>
    );
}
